import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import get from "lodash/get";
import { useFeatures } from "flagged";
import {
  EMPTY_STRING,
  DEPLOY_STATUSES,
  BACKEND_FAILURE_STATUS,
  BACKEND_INITIAL_STATUS,
  FRONTEND_FAILURE_STATUS,
  FRONTEND_INITIAL_STATUS,
  REGEX,
  DEFAULT_LOGO_URL,
  ZERO,
  DEFAULT_BRAND_LOGO_URL,
} from "../../../../constants";
import {
  selectSelectedApp,
  selectApplicationsTemplates,
  createApplication,
  updateApplication,
} from "../../../../pages/Applications";
import { fetchDataModels } from "../../../../pages/DataModels";
import { DRAWER_SIZE } from "../drawers.constants";
import Drawer, {
  selectSelectedDrawer,
} from "../../../../components/organisms/Drawer";
import { actions as drawerActions } from "../../../../components/organisms/Drawer/drawers.reducers";
import { actions as appActions } from "../../../../pages/Applications/applications.reducers";
import { getBase64FromFile } from "../../../../helpers";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import SetAppDetail from "./SetAppDetailDrawer";
import ChooseTemplate from "./ChooseTemplateDrawer";
import CustomizeApp from "./CustomizeAppDrawer";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { actions as alertActions } from "../../Alert/alert.reducers";
import {
  UPLOAD_LOGO_SIZE,
  IMAGE_SIZE_EXCEED_MESSAGE,
  VALIDATION_MESSAGES,
} from "../../../../../src/constants";
import { MODALS } from "../../AppModals/modals.constants";

const BRAND_LOGO = "logoWithBrand";
const {
  MIN_CHAR_LENGTH,
  MAX_CHAR_LENGTH,
  NAME_SHOULD_CONTAIN,
  APPLICATION_NAME_IS_REQUIRED,
  BRAND_NAME_MIN_LENGTH,
  BRAND_NAME_MAX_LENGTH,
  BRAND_NAME_IS_REQUIRED,
  SELECT_CLOUD_SERVICE,
} = VALIDATION_MESSAGES;
const { setAlert } = alertActions;
const { S3_BUCKET_VALIDATION } = REGEX;
const { FULL, MD } = DRAWER_SIZE;
const { FAILURE, CREATING_PROJECT } = DEPLOY_STATUSES;
const { UPDATE_APPLICATION, CREATE_APPLICATION } = SPINNER_NAMES;
const { COMING_SOON_MODAL } = MODALS;
const { setShouldShowDeploymentModal, setSelectedModal } = modalActions;

const { setSelectedDrawer } = drawerActions;
const {
  setAppDeployStatus,
  setSelectedAppId,
  setAppPipelineStatus,
  setRecentlySelectedApp,
} = appActions;

const defaultValues = {
  name: EMPTY_STRING,
  brandName: EMPTY_STRING,
  logo: EMPTY_STRING,
  logoWithBrand: EMPTY_STRING,
  primaryColor: "#FF0000",
  secondaryColor: "#008000",
  cloudService: EMPTY_STRING,
  contrast: "#C71585",
};
const initialStatus = {
  frontend: FRONTEND_INITIAL_STATUS,
  backend: BACKEND_INITIAL_STATUS,
};
const failureStatus = {
  frontend: FRONTEND_FAILURE_STATUS,
  backend: BACKEND_FAILURE_STATUS,
};
const SET_APP_NAME = 1;
const CHOOSE_TEMPLATE = 2;
const APP_CUSTOMIZATION = 3;

const Footer = ({ setActiveStep, activeStep, handleReset, isValid }) => {
  const dispatch = useDispatch();
  const selectedApp = useSelector(selectSelectedApp);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  const handleSetTitle = () => {
    if (activeStep === APP_CUSTOMIZATION) return "Create Project";
    if (activeStep === CHOOSE_TEMPLATE)
      return selectedApp ? "Update Template" : "Choose Template";
    else return "Continue";
  };

  useEffect(() => () => dispatch(setAlert()), [dispatch]);

  const handleOnCancel = () => {
    dispatch(setAlert());
    if (activeStep === APP_CUSTOMIZATION) setActiveStep(CHOOSE_TEMPLATE);
    if (activeStep === CHOOSE_TEMPLATE) setActiveStep(SET_APP_NAME);
    if (activeStep === SET_APP_NAME) {
      handleReset();
      dispatch(setSelectedDrawer(null));
    }
  };

  return (
    <div className="p-6 border-t border-gray-700 flex align-center justify-end">
      <button
        type="button"
        className="btn btn-primary-light mr-1"
        onClick={handleOnCancel}
        disabled={isSpinnerActive}
      >
        {activeStep === SET_APP_NAME ? "Cancel" : "Previous"}
      </button>
      <button
        className="btn btn-primary"
        type="submit"
        disabled={isSpinnerActive || !isValid}
      >
        <Spinner
          name={selectedApp ? UPDATE_APPLICATION : CREATE_APPLICATION}
          setIsSpinnerActive={setIsSpinnerActive}
          persistSize
        >
          {handleSetTitle()}
        </Spinner>
      </button>
    </div>
  );
};

const ManageAppDrawer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedApp = useSelector(selectSelectedApp);
  const templates = useSelector(selectApplicationsTemplates);
  const selectedDrawer = useSelector(selectSelectedDrawer);

  const {
    showAppDataModal,
    showApplicationSettings,
    showApplicationDeploymentsTrack,
  } = useFeatures();

  const [activeStep, setActiveStep] = useState(SET_APP_NAME);
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [appLogoBase64, setAppLogoBase64] = useState(DEFAULT_LOGO_URL);
  const [brandLogoBase64, setBrandLogoBase64] = useState(
    DEFAULT_BRAND_LOGO_URL
  );

  const [initialValues, setInitialValues] = useState(defaultValues);
  const [appDetails, setAppDetails] = useState({
    name: EMPTY_STRING,
    cloudService: EMPTY_STRING,
  });

  useEffect(() => {
    if (!templates) return;
    if (selectedApp) setSelectedTemplate(selectedApp.applicationJson);
    else setSelectedTemplate(templates[ZERO]);
  }, [selectedApp, templates]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, MIN_CHAR_LENGTH)
      .max(20, MAX_CHAR_LENGTH)
      .matches(S3_BUCKET_VALIDATION, NAME_SHOULD_CONTAIN)
      .required(APPLICATION_NAME_IS_REQUIRED),
    brandName: Yup.string()
      .trim()
      .required(BRAND_NAME_IS_REQUIRED)
      .min(3, BRAND_NAME_MIN_LENGTH)
      .max(20, BRAND_NAME_MAX_LENGTH),
    cloudService: Yup.string().required(SELECT_CLOUD_SERVICE),
  });

  const updateInitialValues = useCallback(
    (properties) => {
      const {
        brandName,
        logo,
        primaryColor,
        secondaryColor,
        contrast,
        logoWithBrand,
      } = properties;
      const { name, cloudService } = appDetails;
      setInitialValues((prev) => ({
        name,
        cloudService,
        logo: logo || prev.logo,
        logoWithBrand: logoWithBrand || prev.logoWithBrand,
        brandName: brandName || prev.brandName,
        primaryColor: primaryColor || prev.primaryColor,
        secondaryColor: secondaryColor || prev.secondaryColor,
        contrast: contrast || prev.contrast,
      }));
    },
    [appDetails]
  );

  useEffect(() => {
    if (selectedApp) {
      const properties = selectedApp.applicationJson.properties;
      setAppDetails({
        name: properties.name,
        cloudService: properties.cloudService,
      });
      updateInitialValues(properties);
    } else if (selectedTemplate)
      updateInitialValues(selectedTemplate.properties);
  }, [selectedApp, selectedTemplate, updateInitialValues]);

  const handleClose = useCallback(() => {
    dispatch(setSelectedDrawer(null));
  }, [dispatch]);

  const checkApplicationAccessibility = useMemo(
    () =>
      !(
        showAppDataModal &&
        showApplicationSettings &&
        showApplicationDeploymentsTrack
      ),
    [showAppDataModal, showApplicationSettings, showApplicationDeploymentsTrack]
  );

  const handleCreateApplication = useCallback(
    ({
      name,
      brandName,
      logo,
      primaryColor,
      secondaryColor,
      cloudService,
      contrast,
      logoWithBrand,
    }) => {
      brandName = brandName.trim();
      dispatch(
        setAppDeployStatus({
          frontend: CREATING_PROJECT,
          backend: CREATING_PROJECT,
        })
      );
      if (activeStep === APP_CUSTOMIZATION) {
        dispatch(setRecentlySelectedApp());
        if (checkApplicationAccessibility)
          dispatch(setSelectedModal(COMING_SOON_MODAL));
        else {
          const applicationJson = {
            ...selectedTemplate,
            properties: {
              ...selectedTemplate.properties,
              appName: name,
              brandName,
              logo,
              primaryColor,
              secondaryColor,
              contrast,
              logoWithBrand,
            },
          };
          const updatedAppDetails = {
            name,
            cloudService,
            applicationJson,
          };
          dispatch(
            setAppPipelineStatus({
              backend: BACKEND_INITIAL_STATUS,
              frontend: FRONTEND_INITIAL_STATUS,
            })
          );
          dispatch(setShouldShowDeploymentModal(true));
          dispatch(
            selectedApp
              ? updateApplication({
                  id: selectedApp.id,
                  app: updatedAppDetails,
                })
              : createApplication(updatedAppDetails)
          ).then((res) => {
            if (res.payload) {
              const appId = get(res, "payload.id", EMPTY_STRING);
              dispatch(setSelectedAppId(appId));
              dispatch(setRecentlySelectedApp(res.payload));
              dispatch(
                setAppDeployStatus({
                  frontend: CREATING_PROJECT,
                  backend: CREATING_PROJECT,
                })
              );
              dispatch(setAppPipelineStatus(initialStatus));
              dispatch(fetchDataModels());
              navigate(`/applications/${appId}/app-data-modal`);
              dispatch(setSelectedDrawer(null));
            } else {
              dispatch(setAppPipelineStatus(failureStatus));
              dispatch(
                setAppDeployStatus({ frontend: FAILURE, backed: FAILURE })
              );
            }
          });
        }
      } else if (activeStep === CHOOSE_TEMPLATE)
        setActiveStep(APP_CUSTOMIZATION);
      else {
        setAppDetails({ name, cloudService });
        setActiveStep(CHOOSE_TEMPLATE);
      }
    },
    [
      activeStep,
      dispatch,
      navigate,
      selectedApp,
      selectedTemplate,
      checkApplicationAccessibility,
    ]
  );

  const handleImageUpload = async (event, setFieldValue, name) => {
    const file = event.target.files[0];
    if (file.size > UPLOAD_LOGO_SIZE) {
      dispatch(setAlert({ message: IMAGE_SIZE_EXCEED_MESSAGE }));
      return false;
    } else {
      const base64img = await getBase64FromFile(file);
      if (name === BRAND_LOGO) setBrandLogoBase64(base64img);
      else setAppLogoBase64(base64img);
      setFieldValue(name, base64img);
    }
  };

  const handleResetImage = (setFieldValue, type) => {
    const defaultLogo =
      type === BRAND_LOGO ? DEFAULT_BRAND_LOGO_URL : DEFAULT_LOGO_URL;
    if (type === BRAND_LOGO) setBrandLogoBase64(defaultLogo);
    else setAppLogoBase64(defaultLogo);
    setFieldValue(type, defaultLogo);
  };

  return (
    <>
      {initialValues && (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleCreateApplication}
          validateOnMount={true}
        >
          {({
            values,
            setFieldValue,
            handleChange,
            handleBlur,
            handleReset,
            touched,
            errors,
            isValid,
          }) => {
            return (
              <Form>
                <Drawer
                  position="right"
                  heading={
                    selectedApp
                      ? "Update Application"
                      : "Create a New Application"
                  }
                  subHeading={`${selectedApp ? "Update" : "Configure"}
              your application settings.`}
                  handleReset={handleReset}
                  handleClose={handleClose}
                  size={activeStep === CHOOSE_TEMPLATE ? FULL : MD}
                  footer={
                    <Footer
                      setActiveStep={setActiveStep}
                      activeStep={activeStep}
                      handleReset={handleReset}
                      isValid={isValid}
                    />
                  }
                  selectedDrawer={selectedDrawer}
                >
                  {activeStep === SET_APP_NAME && (
                    <SetAppDetail
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      touched={touched}
                      errors={errors}
                    />
                  )}
                  {activeStep === CHOOSE_TEMPLATE && (
                    <ChooseTemplate
                      templates={templates}
                      setSelectedTemplate={setSelectedTemplate}
                      selectedTemplate={selectedTemplate}
                      setFieldValue={setFieldValue}
                      values={values}
                    />
                  )}
                  {activeStep === APP_CUSTOMIZATION && (
                    <CustomizeApp
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      touched={touched}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      appLogoBase64={appLogoBase64}
                      handleImageUpload={handleImageUpload}
                      handleResetImage={handleResetImage}
                      brandLogoBase64={brandLogoBase64}
                    />
                  )}
                </Drawer>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};

export default ManageAppDrawer;
